/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

import { useLDClient, useFlags } from "launchdarkly-react-client-sdk";
import { datadogRum } from "@datadog/browser-rum";

import { Tenant, User, UserContextType } from "./types";

import { buildLdContext, useChatIntegration } from "../../../integrations";
import {
  retrieveAuthenticatedUserInfo,
  getCurrentSelectedTenant,
  getIsSjAdminFromStorage,
  getImpersonatedEmail,
  exitTenantVisit,
  stopImpersonating,
  savePathForRedirect,
} from "../../../utils";
import { getDirectoryClient } from "../../../client";
import { fetchUserProfileData } from "../../../api";
import { useLoginFlow } from "../../../hooks";

import { FullScreenLoader } from "../../../components";
import { isDeveloperProfileEmpty } from "../../../utils/user";
import { LoginAppRoutes, SjAdminAppRoutes } from "../../../constants/routes";

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserContextProviderProps {
  children: ReactNode;
  enableAnonymousChat?: boolean;
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({
  children,
  enableAnonymousChat,
}) => {
  const ldClient = useLDClient();
  const { displayLearnerProfile: displayDeveloperProfile } = useFlags();

  const isSjAdmin = getIsSjAdminFromStorage();
  const impersonatedEmail = getImpersonatedEmail();

  const { handleUnauthedAccess } = useLoginFlow();
  const [user, setUser] = useState<User | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [isTenantAdmin, setIsTenantAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ldIdentified, setLdIdentified] = useState(false);
  const [isTourVisible, setIsTourVisible] = useState<boolean>(
    !localStorage.getItem("dontShowIntroItems")
  );

  // Check to see if a tenant is selected
  useEffect(() => {
    const tenant = getCurrentSelectedTenant();
    if (!tenant) {
      if (isSjAdmin) {
        // Redirect to sj-admin
        window.location.assign(SjAdminAppRoutes.route);
      } else {
        // Redirect to tenant selection page
        window.location.assign(LoginAppRoutes.routes.tenantSelection.route);
      }
    }
  }, []);

  // Initialize chat integration
  useChatIntegration({
    userEmail: user?.email,
    enableAnonymousChat,
  });

  const svcDirectoryClient = getDirectoryClient();

  useEffect(() => {
    // Initialize user data here
    const initializeUser = async () => {
      try {
        const currentTenant = getCurrentSelectedTenant();
        const userInfo = await retrieveAuthenticatedUserInfo();
        if (!userInfo) {
          throw new Error("No authenticated user info found");
        }

        // Set the user/tenant info from auth first
        setUser(userInfo);
        setTenant(currentTenant);

        // Fetch user details from both sources in parallel
        const [svcDirectoryData, railsData] = await Promise.all([
          fetchSvcDirectoryUserFields(),
          fetchRailsUserFields(),
        ]);

        // Combine all user data into a single object and set once
        const completeUser = {
          ...userInfo,
          ...svcDirectoryData,
          railsProfile: railsData,
        };

        setUser(completeUser);
      } catch (error) {
        console.error("Failed to initialize user:", error);
        handleUnauthedAccess();
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, []);

  // Initialize LaunchDarkly and Datadog when user and tenant are available
  useEffect(() => {
    const init3rdPartyUser = async () => {
      if (!ldClient || !user || !selectedTenant) return;

      datadogRum.setUser({
        id: user.userId,
        email: user.email,
        tenant: selectedTenant,
      });

      await ldClient.identify(buildLdContext(user, selectedTenant));
      setLdIdentified(true);
    };

    init3rdPartyUser();
  }, [user, selectedTenant, ldClient]);

  // Every time user or selectedTenant changes, update isTenantAdmin
  useEffect(() => {
    const isUserTenantAdmin = (user: User | null, selectedTenant: string | null) => {
      if (!user || !selectedTenant) return false;

      const tenantRoleInfo = user.tenants?.find((tenant: Tenant) => tenant.name === selectedTenant);
      if (!tenantRoleInfo) return false;

      return !!tenantRoleInfo.roles?.includes("tenant_admin");
    };

    setIsTenantAdmin(isUserTenantAdmin(user, selectedTenant));
  }, [user, selectedTenant]);

  // Wait for LaunchDarkly, user, and developerProfile (async) to be ready.
  // User is built in stages, so the undefined check ensures developerProfile is populated before the redirect check.
  useEffect(() => {
    if (!ldIdentified || !user || user?.developerProfile === undefined) return;

    const handleDeveloperProfileRedirect = () => {
      if (!displayDeveloperProfile || isSjAdmin) return;

      const devProfileRoute = LoginAppRoutes.routes.developerProfile.route;
      const needsDevProfile = isDeveloperProfileEmpty(user.developerProfile);

      if (needsDevProfile) {
        savePathForRedirect(window.location.pathname + window.location.search);
        window.location.assign(devProfileRoute);
      }
    };

    handleDeveloperProfileRedirect();
  }, [displayDeveloperProfile, ldIdentified, user, isSjAdmin]);

  // Function to set the selected tenant
  const setTenant = async (tenant: string | null) => {
    setSelectedTenant(tenant);
  };

  // Fetch & set additional user fields from svc-directory
  const fetchSvcDirectoryUserFields = async () => {
    try {
      const { data } = await svcDirectoryClient.whoAmI();
      // Remove the "tenants" field from svc-directory data to avoid confusion
      if (data && "tenants" in data) {
        delete data.tenants;
      }

      return (data ?? {}) as Partial<User>;
    } catch (error) {
      console.warn("Failed to fetch user info from svc-directory:", error);
    }
  };

  // Fetch additional user fields from rails
  const fetchRailsUserFields = async () => {
    try {
      const data = await fetchUserProfileData();
      return (data ?? {}) as User["railsProfile"];
    } catch (error) {
      console.warn("Failed to fetch user info from rails:", error);
    }
  };

  // Function to toggle the tour visibility
  const toggleTourVisibility = () => {
    const newVisibility = !isTourVisible;
    setIsTourVisible(newVisibility);

    if (newVisibility) {
      localStorage.removeItem("dontShowIntroItems");
    } else {
      localStorage.setItem("dontShowIntroItems", "true");
    }
  };

  // SJ Admin functionality
  const sjAdminLeaveTenant = async () => {
    await exitTenantVisit();
  };

  const sjAdminStopImpersonation = async () => {
    await stopImpersonating(selectedTenant!);
  };

  const value: UserContextType = {
    isLoading,
    isAuthenticated: !!user,
    isSjAdmin,
    impersonatedEmail,
    user,
    selectedTenant,
    isTenantAdmin,
    isTourVisible,
    setUser,
    setTenant,
    toggleTourVisibility,
    sjAdminLeaveTenant,
    sjAdminStopImpersonation,
  };

  // While loading, render a loader element
  if (isLoading || !ldIdentified) {
    return <FullScreenLoader />;
  }

  // Once fully loaded/initialized, provide the context & render the children
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    return {} as UserContextType;
  }
  return context;
};
